import axios, { AxiosHeaders } from 'axios'
import { API_URL, BOLT_BACKEND_URL } from 'constants/env'
import { AuthTokens } from 'types/auth'
import { Paths } from 'types/paths'
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from './sessionStorageService'

let isRefreshing = false
let failedQueue: any[] = []

const processQueue = (error: any, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

// Request interceptor
axios.interceptors.request.use(
  (config) => {
    const token = getAccessToken()

    if (config.baseURL !== BOLT_BACKEND_URL) {
      config.baseURL = API_URL
    }

    // Initialize headers if necessary
    if (!config.headers) {
      config.headers = {} as AxiosHeaders
    }

    // Set necessary headers
    config.headers['Accept'] = 'application/json'
    if (token) config.headers['Authorization'] = 'Bearer ' + token.id

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
axios.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config

    if (error.response.status === 401 && originalRequest.url === `${API_URL}/auth/internal/refresh-token`) {
      clearTokens()
      window.location.href = Paths.LOGIN
      return Promise.reject(error)
    }

    if (error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        try {
          const token = await new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
          originalRequest.headers['Authorization'] = 'Bearer ' + token
          return axios(originalRequest)
        } catch (err) {
          return Promise.reject(err)
        }
      }

      originalRequest._retry = true
      isRefreshing = true

      const refreshToken = getRefreshToken()
      try {
        const result0 = await axios.post(`${API_URL}/auth/internal/refresh-token`, { refreshToken: refreshToken?.id })
        const { status, data } = result0
        if (status === 200) {
          const { token, refreshToken } = data as unknown as AuthTokens
          setTokens({ token, refreshToken })

          axios.defaults.headers.common['Authorization'] = 'Bearer ' + getAccessToken()?.id

          processQueue(null, data.token)

          return axios(originalRequest)
        }

        clearTokens()
        window.location.href = Paths.LOGIN
        return Promise.reject()
      } catch (err1) {
        processQueue(err1, null)
        return Promise.reject(err1)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default axios
